import { MouseEvent, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import useTheme from "@mui/material/styles/useTheme";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import CachedIcon from "@mui/icons-material/Cached";
import SearchIcon from "@mui/icons-material/Search";
import AllInclusiveRoundedIcon from "@mui/icons-material/AllInclusiveRounded";
import BeachAccessRoundedIcon from "@mui/icons-material/BeachAccessRounded";
import WaterDropRoundedIcon from "@mui/icons-material/WaterDropRounded";
import AnchorRoundedIcon from "@mui/icons-material/AnchorRounded";
import WavesRoundedIcon from "@mui/icons-material/WavesRounded";
import FullscreenRoundedIcon from "@mui/icons-material/FullscreenRounded";
import FullscreenExitRoundedIcon from "@mui/icons-material/FullscreenExitRounded";
import BookmarkRoundedIcon from "@mui/icons-material/BookmarkRounded";
import FormatListBulletedRoundedIcon from "@mui/icons-material/FormatListBulletedRounded";
import { MapContainer, Marker, Popup, ZoomControl } from "react-leaflet";
import { LatLngExpression } from "leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import MapBaseLayers, { MAP_MAX_ZOOM, NearbyMapPlace, Coordinates } from "../UI/MapBaseLayers";
import {
  CommunityQuestion,
  getCommunityQuestions,
  submitCommunityObservation,
} from "../../services/communityApi";
import {
  MobileMapFilterId,
  PLACE_CATEGORIES,
  PlaceCategoryId,
  getPlaceCategoryId,
  matchesPlaceCategory,
  selectMobileMapFilter,
} from "./placeCatalog";

export interface MapContext {
  id: string;
  label: string;
  color: string;
  position: LatLngExpression;
}

interface ContextSelectionCardProps {
  contexts: MapContext[];
  selectedContextIds: string[];
  onSelectionChange: (contextIds: string[]) => void;
  onPlaceSelect?: (contextId: string) => void;
  latestRecvByContextId: Record<string, ContextFreshnessData>;
  selectedRangeHours: number;
  onRangeChange: (hours: number) => void;
  groupByContext: boolean;
  onGroupByContextChange: (groupByContext: boolean) => void;
  favoritesOnly?: boolean;
  onShowAllPlaces?: () => void;
  onShowFollowedPlaces?: () => void;
  mobile: boolean;
  mapViewport?: "mobile" | "desktop";
}
type PlaceFilterCategoryId = Exclude<PlaceCategoryId, "all">;
export type ContextFreshness = "unknown" | "fresh" | "stale" | "critical";

export interface ContextFreshnessData {
  latestReceivedAt: string | null | undefined;
  attributeLatestReceivedAt?: Record<string, string | undefined>;
}

export const CONTEXT_STALE_AFTER_MS = 3 * 24 * 60 * 60 * 1000;
export const CONTEXT_CRITICAL_AFTER_MS = 14 * 24 * 60 * 60 * 1000;

export const getContextFreshnessStatus = (
  lastReceivedAt: string | null | undefined,
  now = Date.now()
): ContextFreshness => {
  if (lastReceivedAt === undefined) return "unknown";
  if (lastReceivedAt === null) return "critical";
  const receivedAt = Date.parse(lastReceivedAt);
  if (Number.isNaN(receivedAt)) return "unknown";
  const age = Math.max(0, now - receivedAt);
  if (age > CONTEXT_CRITICAL_AFTER_MS) return "critical";
  if (age > CONTEXT_STALE_AFTER_MS) return "stale";
  return "fresh";
};

export const getContextFreshnessDataStatus = (
  freshnessData: ContextFreshnessData | undefined,
  now = Date.now()
): ContextFreshness => {
  if (!freshnessData) return "unknown";
  const statuses = [
    getContextFreshnessStatus(freshnessData.latestReceivedAt, now),
    ...Object.values(freshnessData.attributeLatestReceivedAt || {})
      .map((receivedAt) => getContextFreshnessStatus(receivedAt, now)),
  ];
  if (statuses.includes("critical")) return "critical";
  if (statuses.includes("stale")) return "stale";
  if (statuses.includes("fresh")) return "fresh";
  return "unknown";
};
export const formatNearbyDistance = (distanceMeters: number): string =>
  `${Math.max(0, Math.round(distanceMeters)).toLocaleString("es-UY")} metros`;

const placeCategoryIcon = (categoryId: PlaceFilterCategoryId): JSX.Element => {
  switch (categoryId) {
    case "beach":
      return <BeachAccessRoundedIcon fontSize="small" />;
    case "stream":
      return <WaterDropRoundedIcon fontSize="small" />;
    case "port":
      return <AnchorRoundedIcon fontSize="small" />;
    case "tide_station":
      return <WavesRoundedIcon fontSize="small" />;
    default:
      return <WavesRoundedIcon fontSize="small" />;
  }
};

const markerIconSvg = (
  isSelected: boolean,
  freshness: ContextFreshness,
  size: number,
  isDark: boolean,
) => {
  const themeColors = isDark
    ? {
        surface: "#102d36",
        ring: "#57d5c4",
        selected: "#8fe6df",
        core: "#061522",
        dot: "#e1f5f2",
        selectedOutline: "#e1f5f2",
        stale: "#e4c27a",
        critical: "#f0a3a3",
        unknown: "#b8ccd2",
      }
    : {
        surface: "#ffffff",
        ring: "#0b7d87",
        selected: "#18a793",
        core: "#e7fffb",
        dot: "#07324a",
        selectedOutline: "#07324a",
        stale: "#a66b00",
        critical: "#b42318",
        unknown: "#607286",
      };
  const stateColor = freshness === "critical"
    ? themeColors.critical
    : freshness === "stale"
      ? themeColors.stale
      : freshness === "unknown"
        ? themeColors.unknown
        : themeColors.ring;
  const markerColor = isSelected ? themeColors.selected : themeColors.surface;
  const outlineColor = isSelected ? themeColors.selectedOutline : stateColor;
  const centerColor = isSelected ? themeColors.core : stateColor;
  const center = size / 2;

  return `
    <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" aria-hidden="true">
      <circle cx="${center}" cy="${center}" r="${size * 0.38}" fill="${markerColor}" stroke="${outlineColor}" stroke-width="${isSelected ? 2 : 1.5}"/>
      <circle cx="${center}" cy="${center}" r="${size * 0.14}" fill="${centerColor}"/>
      <circle cx="${center}" cy="${center}" r="${size * 0.06}" fill="${themeColors.dot}"/>
    </svg>
  `;
};



const ContextSelectionCard = ({
  contexts,
  selectedContextIds,
  onSelectionChange,
  onPlaceSelect,
  latestRecvByContextId,
  selectedRangeHours,
  onRangeChange,
  groupByContext,
  onGroupByContextChange,
  mobile,
  mapViewport = mobile ? "mobile" : "desktop",
  favoritesOnly = false,
  onShowAllPlaces,
  onShowFollowedPlaces,
}: ContextSelectionCardProps) => {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryId, setCategoryId] = useState<PlaceCategoryId>("all");
  const [listOpen, setListOpen] = useState(false);
  const categoryRowRef = useRef<HTMLDivElement | null>(null);
  const categoryDragRef = useRef<{
    startX: number;
    startScrollLeft: number;
    moved: boolean;
    categoryId?: MobileMapFilterId;
  } | null>(null);
  const suppressCategoryClickRef = useRef(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fullscreenError, setFullscreenError] = useState(false);
  const fullscreenErrorTimerRef = useRef<number | null>(null);
  const [nearbyPrompt, setNearbyPrompt] = useState<{
    context: MapContext;
    distanceMeters: number;
    userPosition: Coordinates;
  } | null>(null);
  const [nearbyContributionPosition, setNearbyContributionPosition] = useState<Coordinates | null>(null);
  const [nearbyContributionContext, setNearbyContributionContext] = useState<MapContext | null>(null);
  const [communityQuestions, setCommunityQuestions] = useState<CommunityQuestion[]>([]);
  const [communityAnswers, setCommunityAnswers] = useState<Record<string, string>>({});
  const [communityStatus, setCommunityStatus] = useState<"idle" | "loading" | "submitting" | "success" | "error">("idle");
  const [communityError, setCommunityError] = useState<string | null>(null);

  useEffect(() => {
    const syncFullscreenState = () => {
      setIsFullscreen(document.fullscreenElement === document.documentElement);
    };
    document.addEventListener("fullscreenchange", syncFullscreenState);
    syncFullscreenState();
    return () => {
      document.removeEventListener("fullscreenchange", syncFullscreenState);
      if (fullscreenErrorTimerRef.current !== null) {
        window.clearTimeout(fullscreenErrorTimerRef.current);
      }
    };
  }, []);

  const showFullscreenError = () => {
    setFullscreenError(true);
    if (fullscreenErrorTimerRef.current !== null) {
      window.clearTimeout(fullscreenErrorTimerRef.current);
    }
    fullscreenErrorTimerRef.current = window.setTimeout(() => {
      setFullscreenError(false);
    }, 3500);
  };

  const toggleFullscreen = async () => {
    setFullscreenError(false);
    if (!document.fullscreenEnabled || !document.documentElement.requestFullscreen) {
      showFullscreenError();
      return;
    }
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await document.documentElement.requestFullscreen({ navigationUI: "hide" });
      }
    } catch {
      showFullscreenError();
    }
  };
  const rangeOptions = [
    { label: "6 h", value: 6 },
    { label: "24 h", value: 24 },
    { label: "48 h", value: 48 },
    { label: "1S", value: 24 * 7 }, // 1 semana
    { label: "1M", value: 24 * 30 }, // 1 mes aproximado en horas
    { label: "3M", value: 24 * 90 }, // 3 meses aproximados en horas
    { label: "1A", value: 24 * 365 }, // 1 año aproximado en horas
    { label: "18M", value: 24 * 30 * 18 }, // 18 meses aproximados en horas
    { label: "2A", value: 24 * 365 * 2 }, // 2 años aproximados en horas
  ];

  const handleRangeToggle = (
    _event: MouseEvent<HTMLElement>,
    newValue: number | null
  ) => {
    if (newValue === null) {
      return;
    }
    onRangeChange(newValue);
  };

  const handleGroupingToggle = (
    _event: MouseEvent<HTMLElement>,
    newValue: string | null
  ) => {
    if (newValue === null) {
      return;
    }
    onGroupByContextChange(newValue === "context");
  };

  const handleMapToggle = (contextId: string) => {
    const isSelected = selectedContextIds.includes(contextId);
    if (isSelected) {
      onSelectionChange(selectedContextIds.filter((id) => id !== contextId));
      return;
    }
    onSelectionChange([...selectedContextIds, contextId]);
  };

  const handleMapClick = (contextId: string) => {
    if (mobile) {
      onPlaceSelect?.(contextId);
      return;
    }
    handleMapToggle(contextId);
  };

  const buildMarkerIcon = (context: MapContext, isSelected: boolean) => {
    const freshness = getContextFreshnessDataStatus(latestRecvByContextId[context.id]);
    const size = isSelected ? 30 : 26;
    return L.divIcon({
      className: "dashboard-context-marker",
      html: markerIconSvg(isSelected, freshness, size, theme.palette.mode === "dark"),
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
      popupAnchor: [0, -size / 2],
    });
  };

  // Se mueve ligeramente a la derecha para que La Paloma quede visible por defecto
  const mapCenter = useMemo<LatLngExpression>(() => [-34.92, -55.3], []);

  const normalizedSearchTerm = searchTerm.trim().toLowerCase();

  const filteredContexts = useMemo(
    () =>
      contexts.filter(
        (ctx) =>
          ctx.label.toLowerCase().includes(normalizedSearchTerm)
          && (favoritesOnly || matchesPlaceCategory(ctx.id, categoryId))
      ),
    [categoryId, contexts, favoritesOnly, normalizedSearchTerm]
  );
  const nearbyBeachPlaces = useMemo(
    () =>
      contexts
        .filter((context) => getPlaceCategoryId(context.id) === "beach")
        .flatMap((context): NearbyMapPlace[] => {
          if (
            !Array.isArray(context.position)
            || typeof context.position[0] !== "number"
            || typeof context.position[1] !== "number"
          ) {
            return [];
          }
          return [{
            id: context.id,
            label: context.label,
            position: [context.position[0], context.position[1]],
          }];
        }),
    [contexts],
  );
  const handleNearbyBeach = (
    place: NearbyMapPlace,
    distanceMeters: number,
    userPosition: Coordinates,
  ) => {
    const context = contexts.find((candidate) => candidate.id === place.id);
    if (!context) return;
    setNearbyPrompt({ context, distanceMeters, userPosition });
  };

  const startNearbyContribution = async () => {
    const prompt = nearbyPrompt;
    if (!prompt) return;
    setNearbyPrompt(null);
    setNearbyContributionContext(prompt.context);
    setNearbyContributionPosition(prompt.userPosition);
    setCommunityQuestions([]);
    setCommunityAnswers({});
    setCommunityError(null);
    setCommunityStatus("loading");
    try {
      const selection = await getCommunityQuestions(prompt.context.id);
      setCommunityQuestions(selection.questions);
      setCommunityStatus("idle");
    } catch {
      setCommunityStatus("error");
      setCommunityError("No se pudieron cargar las preguntas de observación.");
    }
  };

  const closeNearbyContribution = () => {
    if (communityStatus === "submitting") return;
    setNearbyContributionContext(null);
    setNearbyContributionPosition(null);
    setCommunityQuestions([]);
    setCommunityAnswers({});
    setCommunityError(null);
    setCommunityStatus("idle");
  };

  const submitNearbyContribution = async () => {
    if (
      !nearbyContributionContext
      || communityQuestions.length === 0
      || communityQuestions.some((question) => !communityAnswers[question.id])
    ) {
      return;
    }
    setCommunityStatus("submitting");
    setCommunityError(null);
    try {
      await Promise.all(
        communityQuestions.map((question) =>
          submitCommunityObservation({
            context_id: nearbyContributionContext.id,
            question_id: question.id,
            answer: communityAnswers[question.id],
            observed_at: new Date().toISOString(),
            place_confirmed: true,
            location_accessed: true,
            latitude: nearbyContributionPosition?.[0],
            longitude: nearbyContributionPosition?.[1],
            idempotency_key: crypto.randomUUID(),
          }),
        ),
      );
      setCommunityStatus("success");
    } catch {
      setCommunityStatus("error");
      setCommunityError("No se pudieron enviar todas las observaciones.");
    }
  };

  const selectedContexts = useMemo(
    () =>
      contexts.filter((context) => selectedContextIds.includes(context.id)),
    [contexts, selectedContextIds]
  );

  const filteredSelectedContexts = useMemo(
    () =>
      selectedContexts.filter((ctx) =>
        ctx.label.toLowerCase().includes(normalizedSearchTerm)
      ),
    [normalizedSearchTerm, selectedContexts]
  );

  const getCategory = (context: MapContext) => {
    const category = PLACE_CATEGORIES.find(
      ({ id }) => id === getPlaceCategoryId(context.id)
    );
    return category?.label ?? "Mareógrafos";
  };

  const categorizedSelected = useMemo(() => {
    const groups: Record<string, MapContext[]> = {};
    filteredSelectedContexts.forEach((ctx) => {
      const cat = getCategory(ctx);
      groups[cat] = groups[cat] ? [...groups[cat], ctx] : [ctx];
    });

    const order = PLACE_CATEGORIES.map(({ label }) => label);
    return Object.entries(groups)
      .sort((a, b) => order.indexOf(a[0]) - order.indexOf(b[0]))
      .map(([category, items]) => ({ category, items }));
  }, [filteredSelectedContexts]);

  const removeContext = (contextId: string) => {
    onSelectionChange(selectedContextIds.filter((id) => id !== contextId));
  };

  const removeCategory = (category: string) => {
    const toRemove = contexts
      .filter((ctx) => getCategory(ctx) === category)
      .map((ctx) => ctx.id);
    onSelectionChange(selectedContextIds.filter((id) => !toRemove.includes(id)));
  };

  const addCategory = (category: string) => {
    const toAdd = contexts
      .filter((ctx) => getCategory(ctx) === category)
      .map((ctx) => ctx.id);
    const merged = Array.from(new Set([...selectedContextIds, ...toAdd]));
    onSelectionChange(merged);
  };

  const applyMobileMapFilter = (filterId: MobileMapFilterId) => {
    const selection = selectMobileMapFilter(filterId, favoritesOnly);
    setCategoryId(selection.categoryId);
    if (selection.showFollowed) onShowFollowedPlaces?.();
    if (selection.showAll) onShowAllPlaces?.();
  };

  const handleCategoryPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    const categoryTarget = event.target instanceof HTMLElement
      ? event.target.closest<HTMLElement>("[data-place-category]")
      : null;
    categoryDragRef.current = {
      startX: event.clientX,
      startScrollLeft: event.currentTarget.scrollLeft,
      moved: false,
      categoryId: categoryTarget?.dataset.placeCategory as MobileMapFilterId | undefined,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handleCategoryPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = categoryDragRef.current;
    if (!drag) return;
    const deltaX = event.clientX - drag.startX;
    if (Math.abs(deltaX) < 4 && !drag.moved) return;
    drag.moved = true;
    event.preventDefault();
    event.currentTarget.scrollLeft = drag.startScrollLeft - deltaX;
  };

  const handleCategoryPointerEnd = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = categoryDragRef.current;
    categoryDragRef.current = null;
    if (drag?.moved) {
      suppressCategoryClickRef.current = true;
      window.setTimeout(() => {
        suppressCategoryClickRef.current = false;
      }, 0);
    } else if (drag?.categoryId) {
      applyMobileMapFilter(drag.categoryId);
    }
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const subtitleSx = {
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    fontWeight: 600,
    color: "text.secondary",
  } as const;

  const nearbyPromptDialog = (
    <Dialog
      open={Boolean(nearbyPrompt)}
      onClose={() => setNearbyPrompt(null)}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        Detectamos que estás cerca de {nearbyPrompt?.context.label}
      </DialogTitle>
      <DialogContent dividers>
        <Typography>
          Estás aproximadamente a {nearbyPrompt ? formatNearbyDistance(nearbyPrompt.distanceMeters) : ""} de esta estación.
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 1.5 }}>
          Si estás allí, tu contribución puede ayudar mucho a describir las condiciones observadas.
          ¿Querés responder unas preguntas?
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setNearbyPrompt(null)}>Ahora no</Button>
        <Button variant="contained" onClick={() => void startNearbyContribution()}>
          Sí, quiero aportar
        </Button>
      </DialogActions>
    </Dialog>
  );

  const nearbyContributionDialog = (
      <Dialog
        open={Boolean(nearbyContributionContext)}
        onClose={closeNearbyContribution}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Aportar observaciones · {nearbyContributionContext?.label}
        </DialogTitle>
        <DialogContent dividers>
          {communityStatus === "loading" && (
            <Stack alignItems="center" spacing={2} py={3}>
              <CircularProgress size={28} />
              <Typography color="text.secondary">
                Cargando preguntas de observación…
              </Typography>
            </Stack>
          )}
          {communityStatus === "success" && (
            <Alert severity="success">
              Gracias. Se enviaron todas las observaciones de la estación.
            </Alert>
          )}
          {communityError && (
            <Alert severity="error" sx={{ mb: communityQuestions.length ? 2 : 0 }}>
              {communityError}
            </Alert>
          )}
          {communityStatus === "idle" && communityQuestions.map((question) => (
            <FormControl key={question.id} component="fieldset" fullWidth sx={{ mb: 2 }}>
              <FormLabel component="legend">{question.prompt}</FormLabel>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                {question.guidance}
              </Typography>
              <RadioGroup
                value={communityAnswers[question.id] ?? ""}
                onChange={(event) => {
                  setCommunityAnswers((current) => ({
                    ...current,
                    [question.id]: event.target.value,
                  }));
                }}
              >
                {question.options.map((option) => (
                  <FormControlLabel
                    key={option.id}
                    value={option.id}
                    control={<Radio />}
                    label={option.label}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeNearbyContribution} disabled={communityStatus === "submitting"}>
            {communityStatus === "success" ? "Cerrar" : "Cancelar"}
          </Button>
          {communityStatus === "idle" && (
            <Button
              variant="contained"
              onClick={submitNearbyContribution}
              disabled={
                communityQuestions.length === 0
                || communityQuestions.some((question) => !communityAnswers[question.id])
              }
            >
              Enviar observaciones
            </Button>
          )}
          {communityStatus === "submitting" && <CircularProgress size={24} />}
        </DialogActions>
      </Dialog>
  );
  if (mobile) {
    const categoryButtons: Array<{
      id: MobileMapFilterId;
      label: string;
      icon: JSX.Element;
    }> = [
      {
        id: "all",
        label: "Todos",
        icon: <AllInclusiveRoundedIcon fontSize="small" />,
      },
      {
        id: "followed",
        label: "Seguidos",
        icon: <BookmarkRoundedIcon fontSize="small" />,
      },
      {
        id: "beach",
        label: "Playas",
        icon: placeCategoryIcon("beach"),
      },
      {
        id: "stream",
        label: "Arroyos",
        icon: placeCategoryIcon("stream"),
      },
      {
        id: "port",
        label: "Puertos (ANP)",
        icon: placeCategoryIcon("port"),
      },
      {
        id: "tide_station",
        label: "Mareógrafos",
        icon: placeCategoryIcon("tide_station"),
      },
      {
        id: "shn_argentina",
        label: "SHN Argentina",
        icon: placeCategoryIcon("shn_argentina"),
      },
    ];

    return (
      <Box
        sx={{
          position: mapViewport === "mobile" ? "fixed" : "relative",
          ...(mapViewport === "mobile"
            ? { inset: 0, width: "100vw", height: "100vh" }
            : {
                width: "100%",
                height: "calc(100vh - 64px)",
                minHeight: 560,
              }),
          zIndex: 0,
          overflow: "hidden",
          bgcolor: "background.default",
          "& .leaflet-container": {
            width: "100%",
            height: "100%",
            background: theme.palette.mode === "dark" ? "#102d36" : "#b9d8df",
          },
        }}
      >
        <MapContainer
          className="proteo-map"
          attributionControl={false}
          zoomControl={false}
          center={mapCenter}
          zoom={9}
          minZoom={7}
          maxZoom={MAP_MAX_ZOOM}
          style={{ height: "100%", width: "100%" }}
        >
          {mapViewport === "desktop" && <ZoomControl position="bottomleft" />}
          <MapBaseLayers nearbyPlaces={nearbyBeachPlaces} onNearbyPlace={handleNearbyBeach} />
          {filteredContexts.map((context) => {
            const icon = buildMarkerIcon(context, false);
            return (
              <Marker
                key={context.id}
                position={context.position}
                icon={icon}
                title={`${context.label}, abrir estación`}
                alt={context.label}
                eventHandlers={{ click: () => handleMapClick(context.id) }}
              >
                {!mobile && <Popup>{context.label}</Popup>}
              </Marker>
            );
          })}
        </MapContainer>
        <Box
          sx={{
            position: "absolute",
            top: "calc(12px + env(safe-area-inset-top))",
            left: 12,
            right: 12,
            zIndex: 500,
            pointerEvents: "none",
          }}
        >
          <Stack spacing={1} sx={{ pointerEvents: "auto", pt: mapViewport === "mobile" ? 7 : 1.5 }}>
            <Box
              ref={categoryRowRef}
              role="group"
              aria-label="Categorías de estaciones. Deslizá horizontalmente para ver más"
              onPointerDown={handleCategoryPointerDown}
              onPointerMove={handleCategoryPointerMove}
              onPointerUp={handleCategoryPointerEnd}
              onPointerCancel={handleCategoryPointerEnd}
              onWheel={(event) => {
                if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
                  event.currentTarget.scrollLeft += event.deltaY;
                }
              }}
              sx={{
                display: "flex",
                flexWrap: "nowrap",
                gap: 1,
                width: "100%",
                minWidth: 0,
                overflowX: "scroll",
                overflowY: "hidden",
                pb: 0.5,
                touchAction: "pan-y",
                userSelect: "none",
                cursor: "grab",
                WebkitOverflowScrolling: "touch",
                scrollbarWidth: "none",
                "&:active": { cursor: "grabbing" },
                "&::-webkit-scrollbar": { display: "none" },
              }}
            >
              {categoryButtons.map((category) => {
                const selected = category.id === "followed"
                  ? favoritesOnly
                  : !favoritesOnly && categoryId === category.id;
                return (
                  <ToggleButton
                    key={category.id}
                    value={category.id}
                    selected={selected}
                    aria-pressed={selected}
                    aria-label={category.label}
                    data-place-category={category.id}
                    onClick={() => {
                      if (suppressCategoryClickRef.current) {
                        suppressCategoryClickRef.current = false;
                        return;
                      }
                      applyMobileMapFilter(category.id);
                    }}
                    sx={{
                      flexShrink: 0,
                      minHeight: 44,
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 6,
                      bgcolor: "var(--proteo-map-control-surface)",
                      "&.Mui-selected": {
                        bgcolor: theme.palette.mode === "dark"
                          ? "rgba(166, 232, 210, 0.26)"
                          : "rgba(22, 105, 81, 0.15)",
                        color: theme.palette.mode === "dark" ? "#a6e8d2" : "#166951",
                      },
                    }}
                  >
                    <Box component="span" sx={{ display: "inline-flex", mr: 0.5 }}>
                      {category.icon}
                    </Box>
                    {category.label}
                  </ToggleButton>
                );
              })}
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography
                variant="caption"
                sx={{
                  px: 1.25,
                  py: 0.5,
                  borderRadius: 4,
                  color: "text.primary",
                  bgcolor: "var(--proteo-map-control-surface)",
                  backdropFilter: "blur(12px)",
                }}
              >
                {filteredContexts.length} {filteredContexts.length === 1 ? "estación" : "estaciones"}
              </Typography>
              <IconButton
                aria-label="Ver lista de estaciones"
                onClick={() => setListOpen(true)}
                sx={{
                  minWidth: 44,
                  minHeight: 44,
                  color: "text.primary",
                  bgcolor: "var(--proteo-map-control-surface)",
                  backdropFilter: "blur(12px)",
                }}
              >
                <FormatListBulletedRoundedIcon />
              </IconButton>
              <IconButton
                aria-label={isFullscreen ? "Salir de pantalla completa" : "Ver en pantalla completa"}
                onClick={() => void toggleFullscreen()}
                sx={{
                  minWidth: 44,
                  minHeight: 44,
                  color: "text.primary",
                  bgcolor: "var(--proteo-map-control-surface)",
                  backdropFilter: "blur(12px)",
                }}
              >
                {isFullscreen ? <FullscreenExitRoundedIcon /> : <FullscreenRoundedIcon />}
              </IconButton>
            {fullscreenError && (
              <Typography
                role="status"
                variant="caption"
                sx={{
                  px: 1.25,
                  py: 0.75,
                  borderRadius: 3,
                  color: "text.primary",
                  bgcolor: theme.palette.mode === "dark"
                    ? "rgba(16, 45, 54, 0.82)"
                    : "rgba(252, 254, 253, 0.9)",
                  backdropFilter: "blur(12px)",
                }}
              >
                La pantalla completa no está disponible en este navegador.
              </Typography>
            )}
            </Stack>
          </Stack>
        </Box>
        <Dialog
          open={listOpen}
          onClose={() => setListOpen(false)}
          fullWidth
          maxWidth="sm"
          aria-labelledby="place-list-title"
        >
          <DialogTitle id="place-list-title">Estaciones encontradas</DialogTitle>
          <DialogContent dividers>
            <List disablePadding>
              {filteredContexts.map((context) => (
                <ListItemButton
                  key={context.id}
                  onClick={() => {
                    setListOpen(false);
                    onPlaceSelect?.(context.id);
                  }}
                  sx={{ minHeight: 56 }}
                >
                  <ListItemText
                    primary={context.label}
                    secondary={PLACE_CATEGORIES.find(
                      ({ id }) => id === getPlaceCategoryId(context.id)
                    )?.label}
                  />
                </ListItemButton>
              ))}
              {filteredContexts.length === 0 && (
                <Typography color="text.secondary" sx={{ py: 2 }}>
                  No encontramos estaciones con esta búsqueda.
                </Typography>
              )}
            </List>
          </DialogContent>
        </Dialog>
        {nearbyPromptDialog}
        {nearbyContributionDialog}
      </Box>
    );

  }
  return (
    <Stack spacing={3} sx={{ width: "100%" }}>
      <Typography variant="body2" color="text.secondary">
        Activa o desactiva los puntos de monitoreo para explorar sus datos
        históricos y elige el rango temporal a visualizar.
        <br />
        <strong>Nota:</strong> el filtrado usa <code>recvTime</code> (momento de recepción) y no el timestamp original de la muestra.
      </Typography>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ xs: "flex-start", md: "center" }}
      >
        <Stack spacing={1}>
          <Typography variant="subtitle2" sx={subtitleSx}>
            RANGO TEMPORAL
          </Typography>
          <ToggleButtonGroup
            exclusive
            value={selectedRangeHours}
            onChange={handleRangeToggle}
            size="small"
            aria-label="Rango temporal"
          >
            {rangeOptions.map((option) => (
              <ToggleButton key={option.value} value={option.value} sx={{ px: 2 }}>
                {option.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>
        <Stack spacing={1}>
          <Typography variant="subtitle2" sx={subtitleSx}>
            MODO DE VISUALIZACIÓN
          </Typography>
          <ToggleButtonGroup
            exclusive
            value={groupByContext ? "context" : "magnitude"}
            onChange={handleGroupingToggle}
            size="small"
            aria-label="Modo de visualización"
          >
            <ToggleButton value="magnitude" sx={{ px: 2 }}>
              Por Parámetro
            </ToggleButton>
            <ToggleButton value="context" sx={{ px: 2 }}>
              Por estación
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        <Stack spacing={1} sx={{ width: { xs: "100%", md: 280 } }}>
          <Typography variant="subtitle2" sx={subtitleSx}>
            BUSCAR ESTACIÓN
          </Typography>
          <TextField
            size="small"
            placeholder="Filtrar por nombre..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Stack>
      </Stack>

      <Divider flexItem />

      <Box>
        <Stack spacing={1} mb={2}>
          <Typography variant="subtitle2" sx={subtitleSx}>
            ESTACIONES ACTIVAS
          </Typography>
          {filteredSelectedContexts.length ? (
            <Stack spacing={1.5}>
              {categorizedSelected.map(({ category, items }) => (
                <Stack key={category} spacing={0.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      label={`${category} (${items.length})`}
                      onDelete={() => removeCategory(category)}
                      deleteIcon={<span style={{ fontWeight: 600 }}>×</span>}
                      sx={{ fontWeight: 600 }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => addCategory(category)}
                      aria-label={`Activar ${category}`}
                    >
                      <CachedIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {items.map((context) => (
                      <Chip
                        key={context.id}
                        label={context.label}
                        icon={<FiberManualRecordIcon sx={{ color: context.color }} />}
                        onDelete={() => removeContext(context.id)}
                        sx={{
                          bgcolor: (theme) =>
                            theme.palette.mode === "dark"
                              ? `${context.color}55`
                              : `${context.color}1A`,
                          color: (theme) =>
                            theme.palette.mode === "dark"
                              ? theme.palette.text.primary
                              : "text.primary",
                          border: (theme) =>
                            theme.palette.mode === "dark"
                              ? `1px solid ${context.color}99`
                              : "none",
                          "& .MuiChip-icon": {
                            color: context.color,
                          },
                        }}
                      />
                    ))}
                  </Stack>
                </Stack>
              ))}
            </Stack>
          ) : (
            <Chip label={searchTerm ? "Sin coincidencias con el filtro" : "Sin estaciones seleccionadas"} />
          )}
        </Stack>
        <Typography variant="subtitle2" sx={subtitleSx}>
          SELECCIÓN DE ESTACIÓN
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Selecciona un punto en el mapa para activarlo o desactivarlo.
        </Typography>
      </Box>

      <Box
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <MapContainer
          className="proteo-map"
          attributionControl={false}
          zoomControl={false}
          center={mapCenter}
          zoom={9}
          minZoom={7}
          maxZoom={MAP_MAX_ZOOM}
          style={{ height: "420px", width: "100%" }}
        >
          <MapBaseLayers nearbyPlaces={nearbyBeachPlaces} onNearbyPlace={handleNearbyBeach} />
          {filteredContexts.map((context) => {
            const isSelected = selectedContextIds.includes(context.id);
            const icon = buildMarkerIcon(context, isSelected);
            return (
              <Marker
                key={context.id}
                position={context.position}
                icon={icon}
                eventHandlers={{ click: () => handleMapToggle(context.id) }}
              >
                {!mobile && <Popup>{context.label}</Popup>}
              </Marker>
            );
          })}
        </MapContainer>
      </Box>
      {nearbyPromptDialog}
      {nearbyContributionDialog}
    </Stack>
  );
};

export default ContextSelectionCard;
